import type { User, Thread, Message, Note, AuditLog, EmailClassification, DashboardStats } from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function request<T>(endpoint: string, options?: RequestInit): Promise<{ ok: boolean; data?: T; error?: string }> {
  const token = localStorage.getItem('signal_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...(options?.headers || {}) },
  });
  const data = await res.json();
  return { ok: res.ok, data, error: data.error };
}

// Auth
export const verifyAuth = () => request<User>('/api/auth/verify', { method: 'GET' });

// Threads
export const getThreads = (params?: {
  status?: string;
  domain?: string;
  category?: string;
  priority?: string;
  assignee?: string;
  limit?: number;
  offset?: number;
}) => {
  const search = new URLSearchParams();
  if (params) Object.entries(params).forEach(([k, v]) => v !== undefined && search.set(k, String(v)));
  return request<Thread[]>(`/api/threads?${search.toString()}`);
};

export const getThread = (threadId: string) => request<{ thread: Thread; messages: Message[]; notes: Note[]; auditLogs: AuditLog[] }>(`/api/threads/${threadId}`);

export const assignThread = (threadId: string, assignee: string) => request(`/api/threads/${threadId}/assign`, { method: 'POST', body: JSON.stringify({ assignee }) });

export const updateThreadStatus = (threadId: string, status: Thread['status']) => request(`/api/threads/${threadId}/status`, { method: 'PUT', body: JSON.stringify({ status }) });

export const markThreadRead = (threadId: string) => request(`/api/threads/${threadId}/read`, { method: 'POST' });

// Messages / Replies
export const replyToThread = (threadId: string, data: { to: string; subject: string; body: string; staff_id: string; signature?: string }) =>
  request(`/api/threads/${threadId}/reply`, { method: 'POST', body: JSON.stringify(data) });

// Notes
export const addNote = (threadId: string, note: string) => request(`/api/threads/${threadId}/notes`, { method: 'POST', body: JSON.stringify({ note }) });

// AI Assist
export const classifyEmail = (data: { subject: string; body: string; from: string; domain: string }) =>
  request<EmailClassification>('/api/ai/classify', { method: 'POST', body: JSON.stringify(data) });

export const summarizeThread = (threadId: string) => request<{ summary: string }>('/api/ai/summarize', { method: 'POST', body: JSON.stringify({ threadId }) });

export const extractActionItems = (threadId: string) => request<{ actions: string[] }>('/api/ai/action-items', { method: 'POST', body: JSON.stringify({ threadId }) });

export const draftReply = (threadId: string, tone?: string) => request<{ draft: string }>('/api/ai/draft-reply', { method: 'POST', body: JSON.stringify({ threadId, tone }) });

// Dashboard
export const getDashboardStats = () => request<DashboardStats>('/api/dashboard');

// Users
export const getUsers = () => request<User[]>('/api/users');

export const createUser = (data: Partial<User>) => request('/api/users', { method: 'POST', body: JSON.stringify(data) });

// Settings
export const getSettings = () => request<Record<string, string>>('/api/settings');

export const updateSetting = (key: string, value: string) => request('/api/settings', { method: 'PUT', body: JSON.stringify({ key, value }) });